<template>
  <div class="control-stepper" :class="{ 'control-stepper--disabled': disabled }" :style="rootStyle">
    <button
      type="button"
      class="control-stepper-btn"
      :disabled="disabled"
      @pointerdown="onPress(-1, $event)"
      @pointerup="onRelease"
      @pointercancel="onRelease"
      @lostpointercapture="onRelease"
      @keydown.enter.prevent="emit('step', -1)"
      @keydown.space.prevent="emit('step', -1)">
      <ControlIcon name="minus" size="1.25em" />
    </button>

    <span class="control-stepper-value">{{ valueText }}</span>

    <button
      type="button"
      class="control-stepper-btn control-stepper-btn--accent"
      :disabled="disabled"
      @pointerdown="onPress(1, $event)"
      @pointerup="onRelease"
      @pointercancel="onRelease"
      @lostpointercapture="onRelease"
      @keydown.enter.prevent="emit('step', 1)"
      @keydown.space.prevent="emit('step', 1)">
      <ControlIcon name="plus" size="1.25em" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue';
import ControlIcon from '../device-icons/ControlIcon.vue';
import { sheetAccentStyle } from './sheetAccent';

const props = withDefaults(
  defineProps<{
    /** The number as it should read (`21.5 °C`, `14:00`, `3 / 5`). */
    valueText: string;
    /** Press-and-hold repeats the step while held — the light's dim-hold rhythm. */
    hold?: boolean;
    disabled?: boolean;
    /** Overrides the sheet's accent. */
    accent?: string;
  }>(),
  { hold: false, disabled: false },
);

const emit = defineEmits<{
  step: [delta: 1 | -1];
}>();

/** The light's own hold cadence (`LightWidget.onDimHoldStart`). */
const HOLD_INTERVAL_MS = 150;

const rootStyle = computed(() => sheetAccentStyle(props.accent));

const pressed = ref(false);
let holdTimer: number | null = null;

function onPress(delta: 1 | -1, event: PointerEvent) {
  if (props.disabled) return;
  // One physical press fires pointerdown AND mousedown; the guard keeps the
  // second one from restarting the cadence mid-hold (the light's own rule).
  if (pressed.value) return;
  pressed.value = true;
  // Capture the pointer: the button's active-state shrink or a small drift
  // would otherwise fire pointerleave and cut the hold after the first step —
  // the Electron hold regression the light's ± buttons already document.
  try {
    (event.currentTarget as HTMLElement | null)?.setPointerCapture(event.pointerId);
  } catch {
    /* noop */
  }
  emit('step', delta);
  if (!props.hold) return;
  holdTimer = window.setInterval(() => emit('step', delta), HOLD_INTERVAL_MS);
}

function onRelease() {
  if (!pressed.value) return; // pointerup AND pointercancel/lostcapture both fire for one release
  pressed.value = false;
  if (holdTimer !== null) {
    window.clearInterval(holdTimer);
    holdTimer = null;
  }
}

// A hold must not outlive the sheet it is in.
onBeforeUnmount(onRelease);
</script>

<style scoped>
.control-stepper {
  /* The sheet's accent, or the light accent when nothing has set one. */
  --stepper-accent: var(--sheet-accent, var(--device-color-light));
  display: flex;
  align-items: center;
  justify-content: center;
  gap: calc(0.75rem * var(--ui-space-factor));
}

.control-stepper--disabled {
  opacity: 0.45;
}

.control-stepper-btn {
  width: 2.5rem;
  height: 2.5rem;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1.5px solid var(--glass-edge);
  background: rgba(148, 163, 184, 0.1);
  color: rgb(var(--gray-600));
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s,
    color 0.15s,
    transform 0.1s;
  /* The hold gesture owns this button: a drag that starts here must repeat the
     step, never scroll the sheet. */
  touch-action: none;
}

.dark .control-stepper-btn {
  background: rgba(255, 255, 255, 0.03);
  color: rgb(var(--gray-300));
}

.control-stepper-btn--accent {
  border-color: color-mix(in srgb, var(--stepper-accent) 55%, transparent);
  background: color-mix(in srgb, var(--stepper-accent) 13%, transparent);
  color: var(--stepper-accent);
}

.control-stepper-btn:active:not(:disabled) {
  transform: scale(0.94);
}

.control-stepper-btn:disabled {
  cursor: default;
  opacity: 0.5;
}

.control-stepper-value {
  min-width: 4.5em;
  text-align: center;
  font-size: 1.0625rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
</style>
